/* eslint-disable @typescript-eslint/ban-ts-comment */
import spinner from 'assets/spinner.svg'
import { useLoadFormsAndDocuments } from 'queries/forms/useLoadForms'
import { useAddGoogleDriveSettings } from 'queries/integrations/google_drive/useAddGoogleDriveSettings'
import { useDeleteGoogleDriveSettings } from 'queries/integrations/google_drive/useDeleteGoogleDriveSettings'
import { useLoadGoogleDriveSettings } from 'queries/integrations/google_drive/useLoadGoogleDriveSettings'
import { useUpdateGoogleDriveSettings } from 'queries/integrations/google_drive/useUpdateGoogleDriveSettings'
import * as React from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import Button from 'ui/atoms/Button'
import ChevronDownMenu from 'ui/atoms/ChevronDownMenu'
import Flex from 'ui/atoms/Flex'
import Space from 'ui/atoms/Space'
import Table from 'ui/atoms/Table'
import ModalDialog from 'ui/ModalDialog'
import BaseField from 'ui/molecules/BaseField'
import TextField from 'ui/molecules/TextField'
import Page from 'ui/organisms/Page'
import Select from 'ui/Select'

import googleDriveLogo from '@/assets/google-drive-logo.png'
import useSelectPagination from '@/components/SendResource/hooks/useSelectPagination'
import { IntegrationsContext } from '@/utils/IntegrationsContext'

const GoogleDriveSettings = () => {
  const { isLoadingOAuth, oauthSignIn, googleUserDetails } =
    React.useContext(IntegrationsContext)
  const isConnected =
    !googleUserDetails?.status || googleUserDetails.status === 'connected'
  const {
    data: driveSettings,
    isLoading: isListLoading,
    isFetching: isListFetching,
    refetch: refetchGoogleDriveSettings,
  } = useLoadGoogleDriveSettings()
  const { addGoogleDriveSetting, isLoading: isAddLoading } =
    useAddGoogleDriveSettings()
  const { updateGoogleDriveSetting, isLoading: isUpdateLoading } =
    useUpdateGoogleDriveSettings()
  const { deleteGoogleDriveSetting, isLoading: isDeleteLoading } =
    useDeleteGoogleDriveSettings()
  const [selectedSetting, setSelectedSetting] = React.useState<{
    id?: string
    folder_name: string
    forms: string[]
    documents: string[]
  } | null>(null)
  const [selectedForms, setSelectedForms] = React.useState<
    {
      label: string
      value: string
      type: 'form' | 'pdf'
    }[]
  >([])
  const [selectedDocuments, setSelectedDocuments] = React.useState<
    {
      label: string
      value: string
      type: 'pdf'
    }[]
  >([])
  const [hasMore, setHasMore] = React.useState(false)
  const { params, onInputChange, onMenuScrollToBottom, setOptions, options } =
    useSelectPagination<{
      label: string
      value: string
      type?: 'form' | 'pdf'
    }>(hasMore)
  const {
    data: forms,
    isLoading: formsLoading,
    isFetching: formsFetching,
  } = useLoadFormsAndDocuments(
    { ...params },
    {
      keepPreviousData: true,
    }
  )

  React.useEffect(() => {
    setHasMore(Boolean(forms?.next))
  }, [forms?.next])

  React.useEffect(() => {
    setOptions((options) => [
      ...options,
      ...(forms?.results?.map((form) => ({
        label: form.name,
        value: form.id,
        type: form.type,
      })) || []),
    ])
  }, [forms, setOptions])

  const onClear = () => {
    setSelectedDocuments([])
    setSelectedForms([])
    setSelectedSetting(null)
  }

  const isLoading = isListLoading || isDeleteLoading || isListFetching

  return (
    <Page
      withBackButton
      helmetTitle="Google Drive"
      titleComponent={<GoogleDriveLogo />}
    >
      <Space>
        <Flex $horizontalSpacing="large" justify="between">
          <Button
            $primary
            disabled={!isConnected}
            onClick={() =>
              setSelectedSetting({
                folder_name: '',
                forms: [],
                documents: [],
              })
            }
          >
            Add
          </Button>
          {!isConnected && (
            <Button $softDanger loading={isLoadingOAuth} onClick={oauthSignIn}>
              Reconnect to {googleUserDetails?.email || 'Google Drive'}
            </Button>
          )}
        </Flex>

        <Table>
          <Table.Head columns={['FOLDER NAME', 'FORMS', '']} />

          <Table.Body>
            {isLoading && (
              <Table.Row>
                <Table.Column colspan={3}>Loading...</Table.Column>
              </Table.Row>
            )}

            {!isLoading && (driveSettings?.length || 0) === 0 && (
              <Table.Row>
                <Table.Column colspan={3}>No Results Found</Table.Column>
              </Table.Row>
            )}

            {!isLoading &&
              driveSettings?.map((setting) => (
                <Table.Row>
                  <Table.Column>{setting.folder_name}</Table.Column>
                  <Table.Column>
                    <div className="flex flex-wrap gap-1.5">
                      {[...setting.forms, ...setting.documents].map(
                        (item: { id: string; name: string }) => (
                          <div
                            className="rounded-xl bg-gray-200/80 px-2 py-0.5 text-gray-600"
                            key={item.id}
                          >
                            {item.name}
                          </div>
                        )
                      )}
                    </div>
                  </Table.Column>
                  <Table.Column>
                    <ChevronDownMenu
                      options={[
                        {
                          label: 'Edit',
                          onClick: () => {
                            setSelectedSetting({
                              id: setting.id,
                              folder_name: setting.folder_name,
                              forms: setting.forms.map((form) => form.id),
                              documents: setting.documents.map(
                                (document) => document.id
                              ),
                            })
                            setSelectedForms(
                              setting.forms.map((form) => ({
                                label: form.name,
                                value: form.id,
                                type: 'form' as const,
                              }))
                            )
                            setSelectedDocuments(
                              setting.documents.map((document) => ({
                                label: document.name,
                                value: document.id,
                                type: 'pdf' as const,
                              }))
                            )
                          },
                        },
                        {
                          label: 'Delete',
                          onClick: () => {
                            void deleteGoogleDriveSetting(setting.id)
                          },
                        },
                      ]}
                    />
                  </Table.Column>
                </Table.Row>
              ))}
          </Table.Body>
        </Table>
      </Space>
      {selectedSetting ? (
        // @ts-ignore
        <ModalDialog
          opened
          footer={
            <div className="my-4 flex justify-end px-4 sm:px-6">
              <Button
                $primary
                disabled={isAddLoading || isUpdateLoading || formsLoading}
                onClick={async () => {
                  if (!selectedSetting) return
                  if (!selectedSetting.folder_name) {
                    toast.error('Folder name is required')
                    return
                  }
                  if (
                    selectedSetting.forms.length === 0 &&
                    selectedSetting.documents.length === 0
                  ) {
                    toast.error('At least one form or document is required')
                    return
                  }

                  if (selectedSetting?.id) {
                    try {
                      await updateGoogleDriveSetting(
                        selectedSetting.id,
                        selectedSetting
                      )
                      toast.success('Google Drive integration updated')
                    } catch (error) {
                      toast.error('Failed to update Google Drive integration')
                      await refetchGoogleDriveSettings()
                    }
                  } else {
                    try {
                      await addGoogleDriveSetting(selectedSetting)
                      toast.success('Google Drive integration created')
                    } catch (error) {
                      toast.error('Failed to create Google Drive integration')
                      await refetchGoogleDriveSettings()
                    }
                  }
                  onClear()
                }}
              >
                {(isAddLoading || isUpdateLoading) && (
                  <img alt="spinner" className="mr-2" src={spinner} />
                )}
                {selectedSetting?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          }
          onClose={() => {
            onClear()
          }}
          title={
            selectedSetting?.id
              ? 'Edit Google Drive Integration'
              : 'Create Google Drive Integration'
          }
        >
          <Space className="pt-2">
            <TextField
              semibold
              useEvent
              label="Enter Folder Name"
              onChange={(e) => {
                if (typeof e === 'string') {
                  return
                }

                e.persist()
                setSelectedSetting((previous) => {
                  if (!previous) return null
                  return {
                    ...previous,
                    folder_name: e.target.value,
                  }
                })
              }}
              value={selectedSetting?.folder_name}
            />

            <div>
              <BaseField
                semibold
                label="Send Form submissions PDF to Google Drive"
              >
                <Select
                  isMulti
                  showOptions
                  isFetching={formsFetching}
                  menuPosition="fixed"
                  onChange={(
                    values: {
                      value: string
                      label: string
                      type: 'form' | 'pdf'
                    }[]
                  ) => {
                    if (params?.search !== '') {
                      onInputChange('')
                    }
                    setSelectedSetting((previous) => {
                      if (!previous) return null
                      return {
                        ...previous,
                        forms:
                          values
                            ?.filter((v) => v.type === 'form')
                            .map((v) => v.value) || [],
                        documents:
                          values
                            ?.filter((v) => v.type === 'pdf')
                            .map((v) => v.value) || [],
                      }
                    })
                    setSelectedForms(
                      values
                        .filter((v) => v.type === 'form')
                        .map((v) => ({
                          label: v.label,
                          value: v.value,
                          type: 'form' as const,
                        }))
                    )
                    setSelectedDocuments(
                      values
                        .filter((v) => v.type === 'pdf')
                        .map((v) => ({
                          label: v.label,
                          value: v.value,
                          type: 'pdf' as const,
                        }))
                    )
                  }}
                  onMenuScrollToBottom={onMenuScrollToBottom}
                  onType={onInputChange}
                  options={options}
                  placeholder="-- Select Form --"
                  value={[...selectedForms, ...selectedDocuments]}
                />
              </BaseField>
            </div>
          </Space>
        </ModalDialog>
      ) : null}
    </Page>
  )
}

export default GoogleDriveSettings

export const GoogleDriveLogo = styled.img.attrs(() => ({
  src: googleDriveLogo,
  alt: 'google-logo',
  width: 160,
}))`
  display: inline-block;
  margin-left: -3px;
`
